<!-- frontend/src/components/ToolDescriptionContent.vue -->
<template>
  <ContentTemplate
    :mainHeading="$t('toolDescription.mainHeading')"
    :subHeading="$t('toolDescription.subHeading')"
    :sections="translatedSections"
    :showContentNav="true"
  />
</template>

<script>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContentTemplate from '@/components/ContentTemplate.vue';

export default {
  name: 'ToolDescriptionContent',
  components: { ContentTemplate },
  emits: ['sections-content-updated'],

  setup(props, { emit }) {
    const { t, tm, locale } = useI18n();

    // Helper für Bilder mit besserer Fehlerbehandlung
    const getImageMarkup = (imageKey) => {
      if (!imageKey) return '';
      
      const image = tm(imageKey);
      
      // Prüfen ob Bild existiert und filename definiert ist
      if (!image || !image.filename) {
        return '';
      }

      try {
        // Dynamisches Import mit require
        const imagePath = require(`@/assets/images/documentation/${image.filename}`);
        
        return `
          <figure class="documentation-image">
            <img 
              src="${imagePath}" 
              alt="${image.alt || ''}" 
              class="img-fluid"
            />
            ${image.caption ? `<figcaption>${image.caption}</figcaption>` : ''}
          </figure>
        `;
      } catch (error) {
        console.warn(`Image not found: ${image.filename}`);
        return '';
      }
    };

    // Funktion zum Generieren eines Abschnitts mit Unterabschnitten
    const createSection = (sectionKey) => {
      const section = tm(`toolDescription.sections.${sectionKey}`);
      if (!section) return null;

      const imageMarkup = section.image ? 
        getImageMarkup(`toolDescription.sections.${sectionKey}.image`) : '';

      const result = {
        title: t(`toolDescription.sections.${sectionKey}.title`),
        text: section.text + imageMarkup,
        ref: section.ref || sectionKey
      };

      // Unterabschnitte verarbeiten, wenn vorhanden
      if (section.subsections) {
        result.subsections = [];
        
        // Wenn subsections ein Objekt ist (alte Struktur)
        if (typeof section.subsections === 'object' && !Array.isArray(section.subsections)) {
          Object.keys(section.subsections).forEach(subKey => {
            const subsection = section.subsections[subKey];
            const subImageMarkup = subsection.image ? 
              getImageMarkup(`toolDescription.sections.${sectionKey}.subsections.${subKey}.image`) : '';
            
            result.subsections.push({
              title: subsection.title,
              text: subsection.text + subImageMarkup,
              ref: subsection.ref || `${sectionKey}-${subKey}`
            });
          });
        } 
        // Wenn subsections ein Array ist (neue Struktur)
        else if (Array.isArray(section.subsections)) {
          section.subsections.forEach((subsection, index) => {
            const subImageMarkup = subsection.image ? 
              getImageMarkup(`toolDescription.sections.${sectionKey}.subsections.${index}.image`) : '';
            
            result.subsections.push({
              title: subsection.title,
              text: subsection.text + subImageMarkup,
              ref: subsection.ref || `${sectionKey}-sub-${index}`
            });
          });
        }
      }

      return result;
    };

    const translatedSections = computed(() => {
      const sectionKeys = [
        'introduction',
        'gettingStarted',
        'usingTool',
        'solutionDetails',
        'lcaAnalysis',
        'chatbot',
        'dataSources',
        'projectContext',
        'support'
      ];

      return sectionKeys
        .map(key => createSection(key))
        .filter(section => section !== null);
    });

    // Bei Initialisierung und bei Sprachänderung die Abschnitte aktualisieren
    watch(locale, () => {
      emit('sections-content-updated', translatedSections.value);
    }, { immediate: true });

    // Auch die berechneten Abschnitte überwachen (für den Fall, dass sich die Übersetzungen ändern)
    watch(translatedSections, (newSections) => {
      emit('sections-content-updated', newSections);
    }, { deep: true });

    return { translatedSections };
  }
};
</script>

<style>
/* Globale Stile für die Dokumentation */
.documentation-image {
  margin: 2.5rem auto;
  text-align: center;
  max-width: 90%;
}

.documentation-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  display: block;
  margin: 0 auto;
}

.documentation-image img:hover {
  transform: scale(1.02);
}

.documentation-image figcaption {
  margin-top: 1rem;
  font-style: italic;
  color: #666;
  font-size: 0.95rem;
}

/* Stil für den Logo-Container im Projektkontext */
.project-logo {
  text-align: center;
  margin: 2rem auto;
  max-width: 90%;
}

.project-logo img {
  max-width: 100%;
  height: auto;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

/* Hervorhebung von wichtigen Texten */
strong, b {
  color: #333;
  font-weight: 700;
}

/* Verbesserte Listen */
.list-styled li {
  margin-bottom: 12px;
}

.list-styled li strong {
  display: inline-block;
}
</style>